import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import {
  BackendResponse,
  ContestViewEntry,
  Datastore,
  EntryAction,
  User,
} from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor } from '@freelancer/ui/button';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { PictureDisplay, PictureObjectFit } from '@freelancer/ui/picture';
import { EntryStatusApi } from 'api-typings/contests/contests';
import { ContestAwardModalResponse } from './contest-award-modal.types';

@Component({
  selector: 'app-contest-award-modal',
  template: `
    <fl-bit class="AwardModal">
      <fl-heading
        i18n="Award modal title"
        [headingType]="HeadingType.H2"
        [size]="HeadingSize.XSMALL"
        [flMarginBottom]="Margin.SMALL"
      >
        Award this Entry
      </fl-heading>

      <fl-bit class="ModalEntryDetails" [flMarginBottom]="Margin.SMALL">
        <fl-text
          i18n="Award modal entry number"
          [flMarginRight]="Margin.XXXSMALL"
        >
          #{{ entry.number }}
        </fl-text>
        <fl-link [link]="freelancer?.profileUrl">
          {{ freelancer?.displayName }}
        </fl-link>
      </fl-bit>

      <fl-bit class="EntryImageContainer" [flMarginBottom]="Margin.MID">
        <fl-picture
          alt="Entry image"
          class="EntryImageContainer-image"
          [boundedWidth]="true"
          [display]="PictureDisplay.BLOCK"
          [externalSrc]="true"
          [src]="entry.files[0]?.thumbnail900Url"
          [objectFit]="PictureObjectFit.SCALE_DOWN"
        ></fl-picture>
      </fl-bit>
      <fl-button
        i18n="Award modal award button label"
        [busy]="awardBusy"
        [color]="ButtonColor.SUCCESS"
        [display]="'block'"
        (click)="onAwardEntry()"
      >
        Award
      </fl-button>

      <ng-container *ngIf="(entryUpdateStatus$ | async) as response">
        <app-entry-update-error
          *ngIf="response.errorCode"
          [response]="response"
          [source]="EntryAction.AWARD"
          [entry]="entry"
        ></app-entry-update-error>
      </ng-container>

      <!-- TODO: Display multiaward option when multiaward is already implemented -->
      <fl-text
        *ngIf="false"
        class="AwardMultipleText"
        i18n="Award modal select multiple entries contest"
      >
        or
        <fl-link (click)="onAwardMultipleEntries()">
          select multiple winners for this contest
        </fl-link>
      </fl-text>
    </fl-bit>
  `,
  styleUrls: ['./contest-award-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContestAwardModalComponent {
  ButtonColor = ButtonColor;
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;
  Margin = Margin;
  PictureDisplay = PictureDisplay;
  PictureObjectFit = PictureObjectFit;

  EntryAction = EntryAction;

  entryUpdateStatus$: Promise<BackendResponse>;
  awardBusy = false;

  @Input() entry: ContestViewEntry;
  @Input() freelancer: User;

  constructor(
    private modalRef: ModalRef<ContestAwardModalComponent>,
    private router: Router,
    private datastore: Datastore,
  ) {}

  onAwardEntry() {
    this.awardBusy = true;

    const entryDoc = this.datastore.document(
      'contestViewEntries',
      this.entry.id,
    );

    this.entryUpdateStatus$ = entryDoc.update({ status: EntryStatusApi.WON });

    this.entryUpdateStatus$.then(response => {
      this.awardBusy = false;
      if (response.status === 'success') {
        this.router.navigate(['/contest/handover.php'], {
          queryParams: {
            entry_id: this.entry.id,
          },
        });
      }
    });
  }

  onAwardMultipleEntries() {
    this.modalRef.close(ContestAwardModalResponse.AWARD_MULTIPLE);
  }
}
